<script setup>
import { ref } from 'vue';

import AiBotDocumentsAPI from 'dashboard/api/aiBot/documents';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const query = ref('');
const results = ref([]);
const notes = ref([]);
const isSearching = ref(false);
const hasSearched = ref(false);

const percent = score => `${Math.round((score || 0) * 100)}%`;

const runSearch = async () => {
  const q = query.value.trim();
  if (!q) return;

  isSearching.value = true;
  hasSearched.value = false;
  try {
    const { data } = await new AiBotDocumentsAPI(props.bot.id).search(q);
    results.value = data.results || [];
    notes.value = data.notes || [];
    hasSearched.value = true;
  } catch (error) {
    results.value = [];
    notes.value = [];
    hasSearched.value = true;
  } finally {
    isSearching.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="kb-search-preview">
    <form class="flex items-end gap-2" @submit.prevent="runSearch">
      <div class="flex-1">
        <Input
          id="kb-search-input"
          v-model="query"
          :label="$t('AI_AGENT.KNOWLEDGE.SEARCH.INPUT_LABEL')"
          :placeholder="$t('AI_AGENT.KNOWLEDGE.SEARCH.PLACEHOLDER')"
          data-testid="kb-search-input"
          @keyup.enter="runSearch"
        />
      </div>
      <Button
        type="submit"
        size="sm"
        :label="$t('AI_AGENT.KNOWLEDGE.SEARCH.BUTTON')"
        :is-loading="isSearching"
        :disabled="!query.trim()"
        data-testid="kb-search-submit"
      />
    </form>

    <p
      v-if="notes.length"
      class="text-xs text-n-amber-11"
      data-testid="kb-search-notes"
    >
      {{ notes.join(' — ') }}
    </p>

    <div v-if="isSearching" class="flex justify-center py-6">
      <Spinner class="size-4" />
    </div>

    <ul
      v-else-if="hasSearched"
      class="flex flex-col divide-y divide-n-weak"
      data-testid="kb-search-results"
    >
      <li
        v-if="!results.length"
        class="py-6 text-center text-body-main text-n-slate-11"
      >
        {{ $t('AI_AGENT.KNOWLEDGE.SEARCH.EMPTY') }}
      </li>
      <li
        v-for="(result, index) in results"
        :key="index"
        class="flex flex-col gap-1 py-3"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ result.document_name }}
          </span>
          <span
            class="text-xs rounded-md bg-n-solid-3 text-n-slate-11 px-1.5 py-0.5"
          >
            {{ percent(result.score) }}
          </span>
        </div>
        <p class="text-sm text-n-slate-11 line-clamp-3">
          {{ result.chunk }}
        </p>
      </li>
    </ul>
  </div>
</template>
